import { isRecord } from "../document/model";
import { legacyNumber, unsupported } from "./legacy-import-text";
import { isInactiveLegacyDefault } from "./legacy-import-image";
type Layer = { data: Record<string, unknown>; name?: string };
const groupFields = new Set(
  "type version originX originY left top width height scaleX scaleY angle strokeWidth strokeDashArray strokeLineCap strokeDashOffset strokeLineJoin strokeUniform strokeMiterLimit fillRule paintFirst opacity visible objects objectId objectRole customName layerGroupId layerGroupName layerGroupCollapsed layerLocked selectable evented hasControls hasBorders objectCaching dirty perPixelTargetFind subTargetCheck interactive fill stroke backgroundColor globalCompositeOperation flipX flipY skewX skewY shadow clipPath clipToBelow dimensionAspectLocked".split(
    " ",
  ),
);
function origin(value: unknown, end: string) {
  if (value === "center") return 0;
  if (value === end) return -0.5;
  if (value === undefined || value === (end === "right" ? "left" : "top"))
    return 0.5;
  if (typeof value === "number" && Number.isFinite(value)) return 0.5 - value;
  return unsupported("начало координат группы");
}
/** Flat native groups preserve editable children. Non-distributive group effects
 * are rejected before downloading sources or replacing the current document. */
export function legacyLayers(layers: readonly Layer[]): Layer[] {
  const groups = new Set(
    layers
      .filter((l) => l.data.type !== "group" && l.data.layerGroupId)
      .map((l) => String(l.data.layerGroupId)),
  );
  let count = 0;
  type Group = { id: string; name: string; collapsed: boolean };
  const visit = (
    layer: Layer,
    root?: Group,
    ancestors: Group[] = [],
  ): Layer[] => {
    if (++count > 20000 || ancestors.length > 32)
      return unsupported("слишком большую или глубокую группу");
    const d = layer.data;
    if (d.type !== "group") return [layer];
    for (const [key, value] of Object.entries(d))
      if (
        !groupFields.has(key) &&
        value &&
        !isInactiveLegacyDefault(key, value)
      )
        unsupported(`свойство группы «${key}»`);
    if (
      !Array.isArray(d.objects) ||
      !d.objects.length ||
      d.objects.some((o) => !isRecord(o))
    )
      unsupported("пустую или повреждённую группу");
    if (d.objectRole && d.objectRole !== "decorShape")
      unsupported("специальную роль группы");
    if (d.layerGroupId) unsupported("вложенную именованную группу");
    if (
      legacyNumber(d, "opacity", 1) !== 1 ||
      (d.globalCompositeOperation &&
        d.globalCompositeOperation !== "source-over") ||
      d.shadow ||
      d.clipPath ||
      d.clipToBelow ||
      d.backgroundColor
    )
      unsupported("общий эффект или обтравку группы");
    for (const key of ["flipX", "flipY", "skewX", "skewY"])
      if (d[key]) unsupported(`преобразование группы «${key}»`);
    const scale = legacyNumber(d, "scaleX", 1);
    if (scale <= 0 || scale !== legacyNumber(d, "scaleY", 1))
      unsupported("неравномерный масштаб группы");
    if (
      d.layerLocked !== true &&
      (d.selectable === false || d.evented === false)
    )
      unsupported("особые правила выбора группы");
    const angle = legacyNumber(d, "angle"),
      radians = (angle * Math.PI) / 180,
      c = Math.cos(radians),
      s = Math.sin(radians),
      dx =
        (legacyNumber(d, "width") + legacyNumber(d, "strokeWidth", 0)) *
        scale *
        origin(d.originX, "right"),
      dy =
        (legacyNumber(d, "height") + legacyNumber(d, "strokeWidth", 0)) *
        scale *
        origin(d.originY, "bottom"),
      x = legacyNumber(d, "left") + c * dx - s * dy,
      y = legacyNumber(d, "top") + s * dx + c * dy,
      group = {
        id: String(d.objectId || `import-container-${count}`),
        name: String(d.customName || layer.name || "Группа"),
        collapsed: d.layerGroupCollapsed === true,
      };
    if (groups.has(group.id)) unsupported("повторяющийся ID группы");
    groups.add(group.id);
    const top = root ?? group,
      path = [...ancestors, group];
    return (d.objects as Record<string, unknown>[]).flatMap((child, i) => {
      if (child.layerGroupId) unsupported("вложенную именованную группу");
      if (
        child.clipToBelow ||
        (child.globalCompositeOperation &&
          child.globalCompositeOperation !== "source-over")
      )
        unsupported("смешивание или обтравку внутри старой группы");
      if (angle && child.shadow) unsupported("поворот группы с тенью");
      const left = legacyNumber(child, "left"),
        topPosition = legacyNumber(child, "top");
      const data = {
        ...child,
        left: x + scale * (c * left - s * topPosition),
        top: y + scale * (s * left + c * topPosition),
        angle: angle + legacyNumber(child, "angle"),
        scaleX: scale * legacyNumber(child, "scaleX", 1),
        scaleY: scale * legacyNumber(child, "scaleY", 1),
        strokeWidth:
          legacyNumber(child, "strokeWidth", 1) *
          (child.strokeUniform ? scale : 1),
        objectId: child.objectId || `${group.id}-child-${i + 1}`,
        layerLocked: d.layerLocked === true || child.layerLocked === true,
        visible: d.visible !== false && child.visible !== false,
      };
      if (child.type === "group")
        return visit(
          { name: String(child.customName || "Группа"), data },
          top,
          path,
        );
      if (++count > 20000) return unsupported("слишком большую группу");
      return [
        {
          name: String(child.customName || child.type),
          data: {
            ...data,
            layerGroupId: top.id,
            layerGroupName: top.name,
            layerGroupCollapsed: top.collapsed,
            legacyGroupPath: path,
          },
        },
      ];
    });
  };
  return layers.flatMap((layer) => visit(layer));
}
